import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FaMusic, FaPause, FaPlay } from 'react-icons/fa';
import styles from './styles/BackgroundMusic.module.css';
import {
    isMusicPausedByUser,
    MUSIC_PREFERENCE_EVENT,
    saveMusicPausedPreference
} from '../utils/musicPreference';

const BackgroundMusic: React.FC = () => {
    const { pathname } = useLocation();
    const audioRef = useRef<HTMLAudioElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [userPaused, setUserPaused] = useState(isMusicPausedByUser);
    const isHome = pathname === '/';
    const isAdmin = pathname === '/admin' || pathname.startsWith('/admin/');
    const isExcludedRoute = isHome || isAdmin;

    useEffect(() => {
        const syncPreference = (event: Event) => {
            const customEvent = event as CustomEvent<{ paused?: boolean }>;
            setUserPaused(customEvent.detail?.paused ?? isMusicPausedByUser());
        };
        window.addEventListener(MUSIC_PREFERENCE_EVENT, syncPreference);
        return () => window.removeEventListener(MUSIC_PREFERENCE_EVENT, syncPreference);
    }, []);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        if (isExcludedRoute || userPaused) {
            audio.pause();
            return;
        }

        function removeUnlockListeners() {
            document.removeEventListener('pointerdown', unlockMusic);
            document.removeEventListener('keydown', unlockMusic);
        }
        function startMusic() {
            if (!audio || !audio.paused) return;
            audio.play().then(removeUnlockListeners).catch(() => undefined);
        }
        function unlockMusic(event: Event) {
            if (buttonRef.current?.contains(event.target as Node)) return;
            startMusic();
        }

        startMusic();
        document.addEventListener('pointerdown', unlockMusic);
        document.addEventListener('keydown', unlockMusic);
        return removeUnlockListeners;
    }, [isExcludedRoute, userPaused]);

    if (isExcludedRoute) return null;

    const toggleMusic = () => {
        const audio = audioRef.current;
        if (!audio) return;
        if (audio.paused) {
            setUserPaused(false);
            saveMusicPausedPreference(false);
            audio.play().catch(() => undefined);
        } else {
            audio.pause();
            setUserPaused(true);
            saveMusicPausedPreference(true);
        }
    };

    return (
        <>
            <audio
                ref={audioRef}
                src="/media/tango-instrumental.mp3"
                autoPlay={!userPaused}
                loop
                preload="auto"
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
            />
            <button
                ref={buttonRef}
                type="button"
                className={`${styles.control} ${isPlaying ? styles.playing : ''}`}
                onClick={toggleMusic}
                aria-label={isPlaying ? 'Pausar música' : 'Reproducir música'}
                aria-pressed={isPlaying}
            >
                <FaMusic className={styles.note} aria-hidden="true" />
                <span>{isPlaying ? 'Pausar música' : 'Reproducir música'}</span>
                {isPlaying ? <FaPause aria-hidden="true" /> : <FaPlay aria-hidden="true" />}
            </button>
        </>
    );
};

export default BackgroundMusic;
